/* eslint-disable react/prop-types */
/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import i18n from 'i18next';

import { Button, ButtonGroup } from 'uikit/buttons';
import { Loading } from 'uikit/loaders';

import CategoryNavigator from './categoryNavigator';
import DraggableListItem from '../../nestedRows/components/draggableListItem';
import NestedRows from '../../nestedRows/components/nestedRows';
import TreeItemSelectorModalContent from './treeItemSelectorModalContent';
import TreeItemSelectorModalToggleExpandButton from './treeItemSelectorModalToggleExpandButton';
import TreeItemSelectorModalSearchResults from './treeItemSelectorModalSearchResults';

import styles from '../styles/treeItemSelectorModal.module.scss';

const TreeItemSelectorModal = ({
  actionText,
  destination,
  handleToggleExpand,
  isShowingNoSearchResults,
  isShowingRows,
  isShowingSearch,
  isLoading,
  treeItemSelectorModalTree,
  treeItemSelectorModalSearchResults,
  noSearchResultsText,
  onCancelClick,
  onClearSearchClick,
  onItemClick,
  onActionClick,
  onSelectChange,
  onSearchInputChange,
  searchPlaceholder,
  searchValue,
  selectOptions,
  selectValue,
  shouldShowSelect,
  source,
}) => (
  <div className={styles.element}>
    <CategoryNavigator
      className={styles.search}
      shouldShowSelect={shouldShowSelect}
      searchValue={searchValue}
      searchPlaceholder={searchPlaceholder}
      onSearchInputChange={onSearchInputChange}
      onClearSearchClick={onClearSearchClick}
      selectValue={selectValue}
      selectOptions={selectOptions}
      onSelectChange={onSelectChange}
      shouldShowFilters={false}
    />
    {isLoading ? (
      <div className={styles.loading}>
        <Loading />
      </div>
    ) : (
      <>
        {isShowingRows && (
          <NestedRows
            className={styles.rows}
            tree={treeItemSelectorModalTree}
            shouldPreventTransform
            containerHeight="auto"
            offsetPerLevel={24}
            isDragEnabled={false}
            renderItem={(props) => (
              <DraggableListItem
                {...props}
                className={classNames(styles.listItem, {
                  [styles.isSelected]: props.item.id === destination?.id,
                  [styles.isDisabled]: props.item.id === source?.id,
                })}
                item={{ ...props.item, onItemClick }}
                onKebabMenuItemClick={() => null}
                onActionItemClick={() => null}
                onExpand={handleToggleExpand}
                onCollapse={handleToggleExpand}
                components={{
                  Content: TreeItemSelectorModalContent,
                  ToggleExpandButton: TreeItemSelectorModalToggleExpandButton,
                }}
                isDragDisabled
              />
            )}
          />
        )}
        <TreeItemSelectorModalSearchResults
          destination={destination}
          isShowingSearch={isShowingSearch}
          isShowingNoSearchResults={isShowingNoSearchResults}
          treeItemSelectorModalSearchResults={treeItemSelectorModalSearchResults}
          noSearchResultsText={noSearchResultsText}
          onItemClick={onItemClick}
          source={source}
        />
      </>
    )}
    <div className={styles.actions}>
      <ButtonGroup>
        <Button onClick={onCancelClick} text={i18n.t('cancel')} variant="ghost" />
        <Button
          text={actionText}
          onClick={onActionClick}
          color="brand"
          size="medium"
          isDisabled={!destination}
        />
      </ButtonGroup>
    </div>
  </div>
);

TreeItemSelectorModal.propTypes = {
  destination: PropTypes.shape({ id: PropTypes.number }),
  handleToggleExpand: PropTypes.func.isRequired,
  isShowingNoSearchResults: PropTypes.bool,
  isShowingRows: PropTypes.bool,
  isShowingSearch: PropTypes.bool,
  treeItemSelectorModalTree: PropTypes.shape({
    rootId: PropTypes.string,
    items: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  }).isRequired,
  treeItemSelectorModalSearchResults: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      ancestry: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      ),
    }),
  ).isRequired,
  noSearchResultsText: PropTypes.string.isRequired,
  onCancelClick: PropTypes.func.isRequired,
  onClearSearchClick: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onActionClick: PropTypes.func.isRequired,
  onSelectChange: PropTypes.func.isRequired,
  onSearchInputChange: PropTypes.func.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  searchValue: PropTypes.string.isRequired,
  selectOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
    }),
  ),
  selectValue: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  shouldShowSelect: PropTypes.bool,
  source: PropTypes.shape({ id: PropTypes.number }).isRequired,
};

TreeItemSelectorModal.defaultProps = {
  destination: null,
  isShowingNoSearchResults: false,
  isShowingRows: true,
  isShowingSearch: false,
  selectOptions: [],
  selectValue: null,
  shouldShowSelect: false,
};

export default React.memo(TreeItemSelectorModal);
